import "utils/airbrake.ts";
import "utils/ahoy.ts";
import "utils/polyfills.ts";
import "preact/debug";
import "utils/rails-stuff.tsx";

import { observer } from "mobx-react-lite";
import { ExplorerStore } from "explorer/store.ts";
import { ExplorerMap } from "explorer/map.tsx";
import {
  StandAloneHeader,
  StandAloneLink,
} from "components/standalone-header.tsx";
import { ChoroplethRegions } from "explorer/choropleth.tsx";
import { Clusters } from "components/explorer-markercluster.tsx";
import { ExplorerDetailsView } from "components/explorer-detailsview.tsx";
import { AlertList, ListHeader } from "explorer/alert-list.tsx";
import { FilterBar } from "components/filter-bar.tsx";
import { HoverMarker } from "components/explorer-hovermarker.tsx";
import { Toaster } from "react-hot-toast";
import { BottomNav } from "components/BottomNav.tsx";
import { MapControl } from "components/map-control.tsx";
import { CopyToClipboardButton } from "components/copy-to-clipboard-button.tsx";
import {
  HelpButton,
  ToggleChartsButton,
  ToggleListButton,
} from "components/menu-buttons.tsx";
import { showHelp } from "explorer/help.tsx";
import { dynLoaded } from "explorer/explorer-charts.tsx";
import { GeofilterProvider } from "components/geofilter-provider.tsx";
import { RegionPreview } from "components/region-hover.tsx";
import { errorMessageWithReload } from "explorer/errors.tsx";
import { LayerGroup } from "react-leaflet";
import { CopyFacetDataButton } from "components/copy-facet-data-button.tsx";
import { TurboStreamSources } from "utils/turbo-stream-sources.tsx";
import { StreamActions } from "@hotwired/turbo";
import { createRoot } from "react-dom/client";

const explorerStore = new ExplorerStore();
(window as any).store = explorerStore;

StreamActions.reload_store = () => explorerStore.reload();

const ExplorerApp = observer(function ExplorerApp() {
  const contentHeightClass = explorerStore.window.isStandalone
    ? "max-h-[calc(100%-136px)] md:max-h-[calc(100%-80px)]"
    : "max-h-[calc(100%-56px)] md:max-h-full";
  // While the details view (a modal dialog) is open, everything below it is inert: not
  // focusable, not clickable, hidden from screen readers. "contents" keeps the layout as is.
  const behindDialog = !!explorerStore.post;
  return (
    <>
      <Toaster reverseOrder={true} />
      <div class="contents" inert={behindDialog}>
        <ExplorerMap>
          {!explorerStore.choroplethRepaintRequired && (
            <ChoroplethRegions store={explorerStore} />
          )}
          <Clusters store={explorerStore} />
          <HoverMarker store={explorerStore} />
          {window.initData.meiliIndex !== "alerts" && (
            <LayerGroup attribution={`Old Data`} />
          )}
          <MapControl
            position="topleft"
            classNamesInner={
              "flex flex-col space-y-2 text-curious-blue-500 dark:text-curious-blue-500"
            }
          >
            <HelpButton
              classNames="button"
              label={false}
              onClick={() => showHelp(explorerStore)}
            />
            {!explorerStore.window.isStandalone && (
              <StandAloneLink classNames="button" store={explorerStore} />
            )}
            <CopyToClipboardButton classNames={`button`} />
            <CopyFacetDataButton classNames={`button`} store={explorerStore} />
          </MapControl>
          <GeofilterProvider
            setFilterBounds={explorerStore.setFilterBounds}
            padding={[
              /* top right bottom left -- specific values more or less experimental*/
              explorerStore.window.isStandalone ? 80 : 0,
              explorerStore.window.isMd &&
              (explorerStore.showList || explorerStore.showCharts)
                ? window.innerWidth / 3 - 20
                : 0,
              explorerStore.window.isMd ? 0 : 55,
              0,
            ]}
            showRectangle={explorerStore.meili.isPreviewListGeofiltered}
          />
        </ExplorerMap>
      </div>
      <div class="pointer-events-none relative z-abovemap flex h-full flex-1 flex-col">
        <div class="contents" inert={behindDialog}>
          {explorerStore.window.isStandalone && <StandAloneHeader />}
          <div
            class={`flex flex-1 flex-col pl-14 md:flex-row ${contentHeightClass}`}
          >
            <div class="mx-auto flex max-h-full w-full flex-col md:flex-1 ">
              <FilterBar store={explorerStore} />

              <RegionPreview store={explorerStore} />
            </div>
            {/* only the list, charts and their header take touches, the rest of the column is
                map (on phones it spans the whole map, even with the list closed) */}
            <div class="pointer-events-none z-abovemap w-full flex-1 flex-col md:max-w-1/3 max-h-[calc(100%-98px)] md:max-h-full grid grid-cols-1 grid-rows-[3rem_1fr] md:grid-rows-[6.6rem_1fr]">
              <div>
                {explorerStore.window.isMd && (
                  <div class="pointer-events-auto z-abovemap w-full grid grid-cols-2 md:pt-2 pb-2 pr-2 gap-2">
                    <ToggleListButton
                      classNames={`px-2 pt-2 rounded text-black dark:text-white  ${explorerStore.showList ? "bg-slate-400/80 hover:bg-slate-300/80 dark:bg-slate-700/80 dark:hover:bg-slate-800/80" : "bg-white/75 hover:bg-white dark:bg-black/75 dark:hover:bg-black"}`}
                      onClick={() => explorerStore.showListToggle()}
                    />
                    <ToggleChartsButton
                      classNames={`px-2 pt-2 rounded text-black dark:text-white  ${explorerStore.showCharts ? "bg-slate-400/80 hover:bg-slate-300/80 dark:bg-slate-700/80 dark:hover:bg-slate-800/80" : "bg-white/75 hover:bg-white  dark:bg-black/75 dark:hover:bg-black"}`}
                      onClick={() => explorerStore.showChartsToggle()}
                    />
                  </div>
                )}
                <ListHeader store={explorerStore} />
              </div>

              {explorerStore.showList && (
                <AlertList
                  className="pointer-events-auto"
                  store={explorerStore}
                  posts={explorerStore.previewPosts}
                  openFull={(id) => void explorerStore.setPost(id)}
                  hover={(alert) => explorerStore.setListHover(alert)}
                />
              )}
              {explorerStore.showCharts &&
                (dynLoaded.Chart ? (
                  <dynLoaded.Chart
                    className="pointer-events-auto"
                    store={explorerStore}
                  />
                ) : (
                  <div>Loading Chart Library</div>
                ))}
            </div>
          </div>
          {explorerStore.showFooterNav && (
            <div class={"pointer-events-auto z-abovemap"}>
              <BottomNav
                filterActive={explorerStore.filterPanelOpen}
                onFilterClick={explorerStore.filterPanelToggle}
                chartActive={explorerStore.showCharts}
                onChartClick={explorerStore.showChartsToggle}
                listActive={explorerStore.showList}
                onListClick={explorerStore.showListToggle}
                moreActive={false}
                onMoreClick={() => {
                  /* not implemented yet */
                }}
              />
            </div>
          )}
        </div>
        <ExplorerDetailsView store={explorerStore} />
        <TurboStreamSources />
      </div>
    </>
  );
});

const root = createRoot(document.body);
root.render(<ExplorerApp />);

if (!window.initData.meiliKey) {
  errorMessageWithReload(
    "Alert Explorer is currently unable to load alerts.",
    "meiliKey missing. Check if Meilisearch is running.",
    window.initData.meiliKey,
  );
}

document.documentElement.classList.add("js-complete");
